import { useEffect, useState } from "react";
import { MyBet, PublicBet } from "@crashx/shared";
import { useRoundStore } from "../../state/roundStore.js";

export type BetTab = "all" | "previous" | "top" | "my";
export type BetPeriod = "day" | "week" | "month" | "year";
export type BetSort = "win" | "multiplier";
export type BetRowData = PublicBet & Partial<Pick<MyBet, "status" | "placedAt">>;
export function useBetHistory(tab: BetTab, page: number, period: BetPeriod, sort: BetSort, currency: string) {
  const myBets = useRoundStore((state) => state.myBets);
  const roundId = useRoundStore((state) => state.round?.roundId);
  const crashId = useRoundStore((state) => state.lastCrash?.roundId);
  const revision = tab === "my" ? myBets : `${roundId}:${crashId}`;
  const [retry, setRetry] = useState(0);
  const key = `${tab}:${page}:${period}:${sort}:${currency}`;
  const [state, setState] = useState<{ key: string; rows: BetRowData[]; error: boolean; loading: boolean }>({
    key: "",
    rows: [],
    error: false,
    loading: false,
  });
  useEffect(() => {
    if (tab === "all") return;
    const controller = new AbortController();
    const url =
      tab === "my"
        ? `/api/v1/me/bets?page=${page}`
        : tab === "previous"
          ? `/api/v1/game/bets/previous?page=${page}`
          : `/api/v1/game/bets/top?period=${period}&sort=${sort}&currency=${encodeURIComponent(currency)}`;
    setState({ key, rows: [], error: false, loading: true });
    void fetch(url, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("History unavailable");
        const json: unknown = await response.json();
        const rows = tab === "my" ? MyBet.array().parse(json) : PublicBet.array().parse(json);
        if (!controller.signal.aborted) setState({ key, rows, error: false, loading: false });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ key, rows: [], error: true, loading: false });
      });
    return () => controller.abort();
  }, [tab, page, period, sort, currency, revision, key, retry]);
  return {
    rows: state.key === key ? state.rows : [],
    error: state.key === key && state.error,
    loading: state.key !== key || state.loading,
    retry: () => setRetry((value) => value + 1),
  };
}
